import { type Graph, type GraphEdge, type GraphNode, type Vec2 } from '../models/graph';
import { type HintData, type Level, type ProblemType, type StarThresholds } from '../models/level';
import { WORLDS } from '../models/world';
import { dijkstra } from '../algorithms/shortestPath';
import { kruskal } from '../algorithms/mst';
import { edmondsKarp, edmondsKarpResidual } from '../algorithms/maxFlow';
import { findMinCut } from '../algorithms/minCut';
import { maxBipartiteMatching } from '../algorithms/matching';
import { shortestPathInterdiction } from '../algorithms/interdiction';
import { countHamiltonianCycles, optimalTour } from '../algorithms/tsp';

// ─── PRNG ───────────────────────────────────────────────────────────────────
//
// A small, self-contained mulberry32 generator. Seeded and reproducible —
// same seed always produces the same sequence, which is what makes the whole
// generation pipeline deterministic. Deliberately not shared with
// supplyRuns.ts's copy: each module stays independently testable.

export class Rng {
  private state: number;

  constructor(seed: number) {
    this.state = seed >>> 0;
  }

  nextDouble(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = Math.imul(this.state ^ (this.state >>> 15), 1 | this.state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  nextInt(maxExclusive: number): number {
    return Math.floor(this.nextDouble() * maxExclusive);
  }

  nextBool(): boolean {
    return this.nextDouble() < 0.5;
  }
}

function shuffleArray<T>(list: T[], rng: Rng): void {
  for (let i = list.length - 1; i > 0; i--) {
    const j = rng.nextInt(i + 1);
    const tmp = list[i]!;
    list[i] = list[j]!;
    list[j] = tmp;
  }
}

function clampInt(value: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, value));
}

// ─── Difficulty scaling ─────────────────────────────────────────────────────

export interface DifficultyParams {
  readonly nodeCount: number;
  readonly extraEdges: number;
  readonly minWeight: number;
  readonly maxWeight: number;
  readonly minCapacity: number;
  readonly maxCapacity: number;
  readonly interdictionBudget: number;
  readonly layerCount: number;
  readonly nodesPerLayer: number;
  readonly forbidDirectSourceSink: boolean;
}

function lerpInt(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function lerpDouble(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function difficultyParamsForType(type: ProblemType, tier: number): DifficultyParams {
  const t = clampInt(tier, 1, 10);
  const alpha = (t - 1) / 9;

  switch (type) {
    case 'shortestPathPlanar':
    case 'shortestPath':
      return {
        nodeCount: lerpInt(4, 14, alpha),
        extraEdges: lerpInt(1, 6, alpha),
        minWeight: 1,
        maxWeight: lerpDouble(5, 20, alpha),
        minCapacity: 0,
        maxCapacity: 0,
        interdictionBudget: 0,
        layerCount: 0,
        nodesPerLayer: 0,
        forbidDirectSourceSink: t > 5,
      };

    case 'mst':
      return {
        nodeCount: lerpInt(4, 14, alpha),
        extraEdges: lerpInt(2, 8, alpha),
        minWeight: 1,
        maxWeight: lerpDouble(5, 25, alpha),
        minCapacity: 0,
        maxCapacity: 0,
        interdictionBudget: 0,
        layerCount: 0,
        nodesPerLayer: 0,
        forbidDirectSourceSink: false,
      };

    case 'maxFlow':
    case 'minCut': {
      const layers = lerpInt(3, 5, alpha);
      const perLayer = lerpInt(1, 3, alpha);
      const innerLayers = clampInt(layers - 2, 0, layers);
      return {
        nodeCount: 2 + innerLayers * perLayer,
        extraEdges: 0,
        minWeight: 0,
        maxWeight: 0,
        minCapacity: 1,
        maxCapacity: lerpDouble(5, 20, alpha),
        interdictionBudget: 0,
        layerCount: layers,
        nodesPerLayer: perLayer,
        forbidDirectSourceSink: false,
      };
    }

    case 'matching':
      return {
        nodeCount: lerpInt(3, 7, alpha),
        extraEdges: lerpInt(2, 5, alpha),
        minWeight: 0,
        maxWeight: 0,
        minCapacity: 0,
        maxCapacity: 0,
        interdictionBudget: 0,
        layerCount: 0,
        nodesPerLayer: 0,
        forbidDirectSourceSink: false,
      };

    case 'interdiction':
      return {
        nodeCount: lerpInt(4, 12, alpha),
        extraEdges: lerpInt(2, 6, alpha),
        minWeight: 1,
        maxWeight: lerpDouble(5, 15, alpha),
        minCapacity: 0,
        maxCapacity: 0,
        interdictionBudget: lerpInt(1, 3, alpha),
        layerCount: 0,
        nodesPerLayer: 0,
        forbidDirectSourceSink: false,
      };

    case 'tsp':
      return {
        nodeCount: lerpInt(5, 9, alpha),
        extraEdges: lerpInt(2, 5, alpha),
        minWeight: 1,
        maxWeight: lerpDouble(5, 20, alpha),
        minCapacity: 0,
        maxCapacity: 0,
        interdictionBudget: 0,
        layerCount: 0,
        nodesPerLayer: 0,
        forbidDirectSourceSink: false,
      };
  }
}

// ─── Star thresholds ────────────────────────────────────────────────────────

function isLowerBetter(type: ProblemType): boolean {
  switch (type) {
    case 'shortestPathPlanar':
    case 'shortestPath':
    case 'mst':
    case 'minCut':
    case 'tsp':
      return true;
    case 'maxFlow':
    case 'matching':
    case 'interdiction':
      return false;
  }
}

export function computeStarThresholds(type: ProblemType, optimalValue: number): StarThresholds {
  if (isLowerBetter(type)) {
    return {
      threeStar: optimalValue,
      twoStar: optimalValue * 1.2,
      oneStar: optimalValue * 1.5,
    };
  }
  return {
    threeStar: optimalValue,
    twoStar: optimalValue * 0.85,
    oneStar: optimalValue * 0.6,
  };
}

// ─── Hints ──────────────────────────────────────────────────────────────────

function strategyText(type: ProblemType): string {
  switch (type) {
    case 'shortestPathPlanar':
    case 'shortestPath':
      return 'Pip here! Focus on routes with the lowest risk ratings — the less attention from Council patrols, the better!';
    case 'mst':
      return "Okay, okay — we need to connect every neighborhood but we're low on wire! Start with the cheapest connections that don't loop back on themselves.";
    case 'maxFlow':
      return 'Right, so — we need maximum aether throughput! Look for paths from the source to the sink with the fattest pipes. Bottlenecks are the enemy!';
    case 'minCut':
      return "This is the tricky part — find the narrowest chokepoint where cutting conduits completely isolates the Council's supply. Minimum cuts, maximum impact!";
    case 'matching':
      return 'Each operative needs exactly one cover, each cover fits exactly one operative — try assigning the ones with fewer options first so nobody gets left out!';
    case 'interdiction':
      return "Varga's network has a weak spot — target the edges on the shortest path that have no good alternative routes. One good cut and the whole thing crumbles!";
    case 'tsp':
      return 'Rekka here! Every substation gets one visit — no doubling back. Spot any node with only two connections? You have no choice there. Start with those forced legs, then figure out the rest!';
  }
}

export function generateHints(type: ProblemType, optimalEdgeIds: readonly string[]): HintData[] {
  const partialCount = Math.ceil(optimalEdgeIds.length / 2);
  const partialEdges = optimalEdgeIds.slice(0, partialCount);
  const fullEdges = [...optimalEdgeIds];

  return [
    { tier: 0, text: strategyText(type) },
    { tier: 1, highlightEdges: partialEdges.length > 0 ? partialEdges : undefined },
    { tier: 2, highlightEdges: fullEdges.length > 0 ? fullEdges : undefined },
  ];
}

// ─── Layout engine ──────────────────────────────────────────────────────────

const MARGIN = 0.05;
const OVERLAP_THRESHOLD = 0.12;
const MAX_OVERLAP_ITERATIONS = 20;

function withPosition(node: GraphNode, position: Vec2): GraphNode {
  return { ...node, position };
}

function buildAdjacency(
  nodes: readonly GraphNode[],
  edges: readonly GraphEdge[],
  directed: boolean,
): Map<string, string[]> {
  const adjacency = new Map<string, string[]>();
  for (const node of nodes) adjacency.set(node.id, []);

  for (const edge of edges) {
    if (edge.sourceId === edge.targetId) continue;
    if (!adjacency.has(edge.sourceId)) adjacency.set(edge.sourceId, []);
    adjacency.get(edge.sourceId)!.push(edge.targetId);
    if (!directed) {
      if (!adjacency.has(edge.targetId)) adjacency.set(edge.targetId, []);
      adjacency.get(edge.targetId)!.push(edge.sourceId);
    }
  }
  return adjacency;
}

function layerFromSource(
  nodes: readonly GraphNode[],
  adjacency: ReadonlyMap<string, string[]>,
  sourceId: string,
  sinkId: string,
): Map<string, number> {
  const layerOf = new Map<string, number>();
  layerOf.set(sourceId, 0);
  const queue: string[] = [sourceId];

  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentLayer = layerOf.get(current)!;
    for (const neighbour of adjacency.get(current) ?? []) {
      if (!layerOf.has(neighbour)) {
        layerOf.set(neighbour, currentLayer + 1);
        queue.push(neighbour);
      }
    }
  }

  let maxReachableLayer = 0;
  for (const value of layerOf.values()) if (value > maxReachableLayer) maxReachableLayer = value;
  for (const node of nodes) {
    if (!layerOf.has(node.id)) layerOf.set(node.id, maxReachableLayer);
  }

  let sinkLayer = 0;
  for (const value of layerOf.values()) if (value > sinkLayer) sinkLayer = value;
  layerOf.set(sinkId, sinkLayer + 1);

  return layerOf;
}

interface AssignPositionsInput {
  readonly nodes: readonly GraphNode[];
  readonly layerOf: ReadonlyMap<string, number>;
  readonly sourceId: string;
  readonly sinkId: string;
  readonly sourceX: number;
  readonly sinkX: number;
  readonly jitter: number;
  readonly rng: Rng;
}

function assignPositionsFromLayers(input: AssignPositionsInput): GraphNode[] {
  const { nodes, layerOf, sourceId, sinkId, sourceX, sinkX, jitter, rng } = input;

  const byLayer = new Map<number, GraphNode[]>();
  for (const node of nodes) {
    const layer = layerOf.get(node.id)!;
    const list = byLayer.get(layer);
    if (list) list.push(node);
    else byLayer.set(layer, [node]);
  }

  const layers = [...byLayer.keys()].sort((a, b) => a - b);
  const layerCount = layers.length;

  const xForLayer = (layerIndex: number): number => {
    if (layerCount === 1) return (sourceX + sinkX) / 2;
    return sourceX + ((sinkX - sourceX) * layerIndex) / (layerCount - 1);
  };

  let singletonCount = 0;
  for (const list of byLayer.values()) {
    if (list.length === 1 && list[0]!.id !== sourceId && list[0]!.id !== sinkId) singletonCount++;
  }
  const mostlySingletons = singletonCount > layerCount / 2;
  const staggerAmplitude = mostlySingletons ? 0.25 : 0.15;

  const positioned: GraphNode[] = [];

  for (let li = 0; li < layerCount; li++) {
    const layer = layers[li]!;
    const nodesInLayer = byLayer.get(layer)!;
    const count = nodesInLayer.length;
    const x = xForLayer(li);

    for (let i = 0; i < count; i++) {
      const node = nodesInLayer[i]!;

      let y: number;
      if (count === 1) {
        const stagger = li % 2 === 1 ? staggerAmplitude : -staggerAmplitude;
        y = 0.5 + stagger;
      } else {
        y = 0.1 + (0.8 * i) / (count - 1);
      }

      const isPinned = node.id === sourceId || node.id === sinkId;
      const jx = isPinned ? 0 : (rng.nextDouble() - 0.5) * 2 * jitter;
      const jy = isPinned ? 0 : (rng.nextDouble() - 0.5) * 2 * jitter;

      positioned.push(withPosition(node, { x: x + jx, y: y + jy }));
    }
  }

  return positioned;
}

function gridFallback(nodes: readonly GraphNode[], rng: Rng): GraphNode[] {
  const count = nodes.length;
  const cols = Math.max(1, Math.ceil(Math.sqrt(count)));

  const positioned: GraphNode[] = [];
  for (let i = 0; i < count; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const rows = Math.ceil(count / cols);

    const x = cols === 1 ? 0.5 : 0.1 + (0.8 * col) / (cols - 1);
    const y = rows === 1 ? 0.5 : 0.1 + (0.8 * row) / (rows - 1);

    const jx = (rng.nextDouble() - 0.5) * 0.08;
    const jy = (rng.nextDouble() - 0.5) * 0.08;

    positioned.push(withPosition(nodes[i]!, { x: x + jx, y: y + jy }));
  }
  return positioned;
}

function bfsLayerLayout(
  nodes: readonly GraphNode[],
  edges: readonly GraphEdge[],
  rng: Rng,
): GraphNode[] {
  const sourceNode = nodes.find((n) => n.type === 'source');
  const sinkNode = nodes.find((n) => n.type === 'sink');
  if (!sourceNode || !sinkNode) return gridFallback(nodes, rng);

  const adjacency = buildAdjacency(nodes, edges, false);
  const layerOf = layerFromSource(nodes, adjacency, sourceNode.id, sinkNode.id);

  return assignPositionsFromLayers({
    nodes,
    layerOf,
    sourceId: sourceNode.id,
    sinkId: sinkNode.id,
    sourceX: 0.12,
    sinkX: 0.88,
    jitter: 0.06,
    rng,
  });
}

function layeredDagLayout(
  nodes: readonly GraphNode[],
  edges: readonly GraphEdge[],
  rng: Rng,
): GraphNode[] {
  const sourceNode = nodes.find((n) => n.type === 'source');
  const sinkNode = nodes.find((n) => n.type === 'sink');
  if (!sourceNode || !sinkNode) return gridFallback(nodes, rng);

  const adjacency = buildAdjacency(nodes, edges, true);
  const layerOf = layerFromSource(nodes, adjacency, sourceNode.id, sinkNode.id);

  return assignPositionsFromLayers({
    nodes,
    layerOf,
    sourceId: sourceNode.id,
    sinkId: sinkNode.id,
    sourceX: 0.1,
    sinkX: 0.9,
    jitter: 0.04,
    rng,
  });
}

function bipartiteLayout(nodes: readonly GraphNode[]): GraphNode[] {
  const leftNodes: GraphNode[] = [];
  const rightNodes: GraphNode[] = [];
  const normalNodes: GraphNode[] = [];

  for (const node of nodes) {
    if (node.type === 'source') leftNodes.push(node);
    else if (node.type === 'sink') rightNodes.push(node);
    else normalNodes.push(node);
  }

  for (let i = 0; i < normalNodes.length; i++) {
    if (i % 2 === 0) leftNodes.push(normalNodes[i]!);
    else rightNodes.push(normalNodes[i]!);
  }

  const positioned: GraphNode[] = [];
  const placeColumn = (column: readonly GraphNode[], x: number): void => {
    const count = column.length;
    for (let i = 0; i < count; i++) {
      const y = count === 1 ? 0.5 : 0.1 + (0.8 * i) / (count - 1);
      positioned.push(withPosition(column[i]!, { x, y }));
    }
  };

  placeColumn(leftNodes, 0.2);
  placeColumn(rightNodes, 0.8);
  return positioned;
}

function resolveOverlaps(nodes: readonly GraphNode[], rng: Rng): GraphNode[] {
  const positions: Vec2[] = nodes.map((n) => ({ ...n.position }));
  const count = positions.length;

  for (let iter = 0; iter < MAX_OVERLAP_ITERATIONS; iter++) {
    let anyOverlap = false;

    for (let i = 0; i < count; i++) {
      for (let j = i + 1; j < count; j++) {
        const pi = positions[i]!;
        const pj = positions[j]!;
        const dx = pj.x - pi.x;
        const dy = pj.y - pi.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < OVERLAP_THRESHOLD) {
          anyOverlap = true;

          let dirX: number;
          let dirY: number;
          if (dist < 1e-9) {
            dirX = rng.nextBool() ? 1 : -1;
            dirY = 0;
          } else {
            dirX = dx / dist;
            dirY = dy / dist;
          }

          const push = (OVERLAP_THRESHOLD - dist) / 2;
          positions[i] = { x: pi.x - dirX * push, y: pi.y - dirY * push };
          positions[j] = { x: pj.x + dirX * push, y: pj.y + dirY * push };
        }
      }
    }

    if (!anyOverlap) break;
  }

  return nodes.map((node, i) => withPosition(node, positions[i]!));
}

function clampAllPositions(nodes: readonly GraphNode[]): GraphNode[] {
  const lo = MARGIN;
  const hi = 1 - MARGIN;
  return nodes.map((node) =>
    withPosition(node, {
      x: Math.min(hi, Math.max(lo, node.position.x)),
      y: Math.min(hi, Math.max(lo, node.position.y)),
    }),
  );
}

function layoutNodes(
  nodes: readonly GraphNode[],
  edges: readonly GraphEdge[],
  type: ProblemType,
  rng: Rng,
): GraphNode[] {
  if (nodes.length === 0) return [];

  let positioned: GraphNode[];
  switch (type) {
    case 'maxFlow':
    case 'minCut':
      positioned = layeredDagLayout(nodes, edges, rng);
      break;
    case 'matching':
      positioned = bipartiteLayout(nodes);
      break;
    case 'shortestPathPlanar':
    case 'shortestPath':
    case 'mst':
    case 'interdiction':
    case 'tsp':
    default:
      positioned = bfsLayerLayout(nodes, edges, rng);
      break;
  }

  return clampAllPositions(resolveOverlaps(positioned, rng));
}

// ─── Graph generator shared helpers ─────────────────────────────────────────

export interface GeneratorResult {
  readonly graph: Graph;
  readonly optimalValue: number;
  readonly optimalEdgeIds: string[];
}

function letterLabel(index: number): string {
  return String.fromCharCode(65 + index);
}

function letterLabelMod(index: number): string {
  return String.fromCharCode(65 + (index % 26));
}

function makeEdge(
  id: string,
  sourceId: string,
  targetId: string,
  weight = 1,
  capacity = 0,
): GraphEdge {
  return { id, sourceId, targetId, weight, capacity };
}

function randomWeightInt(params: DifficultyParams, rng: Rng): number {
  const range = Math.max(1, Math.round(params.maxWeight - params.minWeight));
  return Math.round(params.minWeight) + rng.nextInt(range);
}

function randomCapacityInt(params: DifficultyParams, rng: Rng): number {
  const capMin = Math.round(params.minCapacity);
  const capMax = Math.round(params.maxCapacity);
  const range = Math.max(1, capMax - capMin);
  return capMin + rng.nextInt(range);
}

function weightOf(edges: readonly GraphEdge[], edgeId: string): number {
  return edges.find((e) => e.id === edgeId)?.weight ?? 0;
}

function capacityOf(edges: readonly GraphEdge[], edgeId: string): number {
  return edges.find((e) => e.id === edgeId)?.capacity ?? 0;
}

function generateWithRetries(
  tryGenerate: (params: DifficultyParams, rng: Rng) => GeneratorResult | null,
  params: DifficultyParams,
  rng: Rng,
  label: string,
): GeneratorResult {
  for (let attempt = 0; attempt < 50; attempt++) {
    if (attempt > 0) {
      for (let i = 0; i < attempt * 7; i++) rng.nextDouble();
    }
    const result = tryGenerate(params, rng);
    if (result) return result;
  }
  throw new Error(`${label}: failed to generate a valid graph after 50 attempts.`);
}

// ─── Shortest path generator (World 1) ──────────────────────────────────────

function hasTwoDistinctPaths(graph: Graph, sourceId: string, sinkId: string): boolean {
  const firstPath = dijkstra(graph, sourceId, sinkId);
  if (firstPath.length === 0) return false;

  const firstSet = new Set(firstPath);
  const reducedEdges = graph.edges.filter((e) => !firstSet.has(e.id));
  const reducedGraph: Graph = { nodes: graph.nodes, edges: reducedEdges, directed: false };

  return dijkstra(reducedGraph, sourceId, sinkId).length > 0;
}

function tryGenerateShortestPath(params: DifficultyParams, rng: Rng): GeneratorResult | null {
  const intermediateCount = clampInt(params.nodeCount - 2, 2, 26);

  const nodes: GraphNode[] = [{ id: 's', label: 'S', position: { x: 0, y: 0 }, type: 'source' }];
  for (let i = 0; i < intermediateCount; i++) {
    nodes.push({ id: `n${i}`, label: letterLabel(i), position: { x: 0, y: 0 }, type: 'normal' });
  }
  nodes.push({ id: 't', label: 'T', position: { x: 0, y: 0 }, type: 'sink' });

  const chainLength = 2 + rng.nextInt(3);
  const intermediateIds = nodes.filter((n) => n.type === 'normal').map((n) => n.id);
  const shuffled = [...intermediateIds];
  shuffleArray(shuffled, rng);
  const basePath = shuffled.slice(0, Math.min(Math.max(chainLength, 1), shuffled.length));

  const edges: GraphEdge[] = [];
  let edgeIndex = 0;
  const weight = (): number => randomWeightInt(params, rng);

  edges.push(makeEdge(`e${edgeIndex++}`, 's', basePath[0]!, weight()));
  for (let i = 0; i < basePath.length - 1; i++) {
    edges.push(makeEdge(`e${edgeIndex++}`, basePath[i]!, basePath[i + 1]!, weight()));
  }
  edges.push(makeEdge(`e${edgeIndex++}`, basePath[basePath.length - 1]!, 't', weight()));

  const existingPairs = new Set<string>();
  const recordPair = (a: string, b: string): void => {
    existingPairs.add(`${a}|${b}`);
    existingPairs.add(`${b}|${a}`);
  };
  for (const e of edges) recordPair(e.sourceId, e.targetId);

  const usedInBase = new Set(basePath);
  const unusedIntermediates = intermediateIds.filter((id) => !usedInBase.has(id));
  shuffleArray(unusedIntermediates, rng);

  if (unusedIntermediates.length > 0) {
    const secondPathLength = 1 + rng.nextInt(Math.min(2, unusedIntermediates.length));
    const secondPath = unusedIntermediates.slice(0, secondPathLength);

    edges.push(makeEdge(`e${edgeIndex++}`, 's', secondPath[0]!, weight()));
    for (let i = 0; i < secondPath.length - 1; i++) {
      edges.push(makeEdge(`e${edgeIndex++}`, secondPath[i]!, secondPath[i + 1]!, weight()));
    }
    edges.push(makeEdge(`e${edgeIndex++}`, secondPath[secondPath.length - 1]!, 't', weight()));
  } else if (!params.forbidDirectSourceSink) {
    edges.push(makeEdge(`e${edgeIndex++}`, 's', 't', weight()));
  } else {
    const reused = basePath[rng.nextInt(basePath.length)]!;
    if (!existingPairs.has(`s|${reused}`)) {
      recordPair('s', reused);
      edges.push(makeEdge(`e${edgeIndex++}`, 's', reused, weight()));
    }
    if (!existingPairs.has(`${reused}|t`)) {
      recordPair(reused, 't');
      edges.push(makeEdge(`e${edgeIndex++}`, reused, 't', weight()));
    }
  }

  const allIds = nodes.map((n) => n.id);
  existingPairs.clear();
  for (const e of edges) recordPair(e.sourceId, e.targetId);

  let extraAdded = 0;
  let safetyBreak = 0;
  while (extraAdded < params.extraEdges && safetyBreak < 200) {
    safetyBreak++;
    const a = allIds[rng.nextInt(allIds.length)]!;
    const b = allIds[rng.nextInt(allIds.length)]!;
    if (a === b) continue;
    if (params.forbidDirectSourceSink && ((a === 's' && b === 't') || (a === 't' && b === 's'))) {
      continue;
    }
    const key = `${a}|${b}`;
    if (existingPairs.has(key)) continue;

    recordPair(a, b);
    edges.push(makeEdge(`e${edgeIndex++}`, a, b, weight()));
    extraAdded++;
  }

  const graph: Graph = { nodes, edges, directed: false };
  const optimalEdgeIds = dijkstra(graph, 's', 't');
  if (optimalEdgeIds.length === 0) return null;

  const optimalWeight = optimalEdgeIds.reduce((sum, id) => sum + weightOf(edges, id), 0);
  const totalWeight = edges.reduce((sum, e) => sum + e.weight, 0);
  if (totalWeight <= 0 || optimalWeight >= totalWeight * 0.5) return null;
  if (!hasTwoDistinctPaths(graph, 's', 't')) return null;

  return { graph, optimalValue: optimalWeight, optimalEdgeIds };
}

export function generateShortestPathGraph(params: DifficultyParams, rng: Rng): GeneratorResult {
  return generateWithRetries(tryGenerateShortestPath, params, rng, 'ShortestPathGenerator');
}

// ─── MST generator (World 2) ────────────────────────────────────────────────

function tryGenerateMst(params: DifficultyParams, rng: Rng): GeneratorResult | null {
  const count = clampInt(params.nodeCount, 4, 26);
  const nodes: GraphNode[] = [];
  for (let i = 0; i < count; i++) {
    nodes.push({ id: `n${i}`, label: letterLabel(i), position: { x: 0, y: 0 }, type: 'normal' });
  }

  const ids = nodes.map((n) => n.id);
  shuffleArray(ids, rng);

  const edges: GraphEdge[] = [];
  let edgeIndex = 0;
  const weight = (): number => randomWeightInt(params, rng);

  for (let i = 0; i < ids.length - 1; i++) {
    edges.push(makeEdge(`e${edgeIndex++}`, ids[i]!, ids[i + 1]!, weight()));
  }

  const existingPairs = new Set<string>();
  for (const e of edges) {
    existingPairs.add(`${e.sourceId}|${e.targetId}`);
    existingPairs.add(`${e.targetId}|${e.sourceId}`);
  }
  for (const id of ids) existingPairs.add(`${id}|${id}`);

  let extraAdded = 0;
  let safetyBreak = 0;
  while (extraAdded < params.extraEdges && safetyBreak < 300) {
    safetyBreak++;
    const a = ids[rng.nextInt(ids.length)]!;
    const b = ids[rng.nextInt(ids.length)]!;
    const key = `${a}|${b}`;
    if (existingPairs.has(key)) continue;

    existingPairs.add(key);
    existingPairs.add(`${b}|${a}`);
    edges.push(makeEdge(`e${edgeIndex++}`, a, b, weight()));
    extraAdded++;
  }

  const graph: Graph = { nodes, edges, directed: false };
  const mstEdgeIds = kruskal(graph);
  if (mstEdgeIds.length !== nodes.length - 1) return null;

  const mstWeight = mstEdgeIds.reduce((sum, id) => sum + weightOf(edges, id), 0);
  const totalWeight = edges.reduce((sum, e) => sum + e.weight, 0);
  if (totalWeight <= 0 || mstWeight >= totalWeight * 0.8) return null;

  return { graph, optimalValue: mstWeight, optimalEdgeIds: mstEdgeIds };
}

export function generateMstGraph(params: DifficultyParams, rng: Rng): GeneratorResult {
  return generateWithRetries(tryGenerateMst, params, rng, 'MSTGenerator');
}

// ─── Max flow generator (World 4) ───────────────────────────────────────────

function tryGenerateMaxFlow(params: DifficultyParams, rng: Rng): GeneratorResult | null {
  const layerCount = clampInt(params.layerCount, 2, 8);
  const nodesPerLayer = clampInt(params.nodesPerLayer, 1, 5);

  const nodes: GraphNode[] = [];
  const layers: string[][] = [];

  nodes.push({ id: 's', label: 'S', position: { x: 0, y: 0 }, type: 'source' });
  layers.push(['s']);

  let nodeIndex = 0;
  for (let layer = 1; layer < layerCount - 1; layer++) {
    const layerIds: string[] = [];
    for (let p = 0; p < nodesPerLayer; p++) {
      const id = `n${nodeIndex}`;
      nodes.push({
        id,
        label: letterLabelMod(nodeIndex),
        position: { x: 0, y: 0 },
        type: 'normal',
      });
      layerIds.push(id);
      nodeIndex++;
    }
    layers.push(layerIds);
  }

  nodes.push({ id: 't', label: 'T', position: { x: 0, y: 0 }, type: 'sink' });
  layers.push(['t']);

  const edges: GraphEdge[] = [];
  let edgeIndex = 0;
  const capacity = (): number => randomCapacityInt(params, rng);
  const existingPairs = new Set<string>();

  for (let l = 0; l < layers.length - 1; l++) {
    const currentLayer = layers[l]!;
    const nextLayer = layers[l + 1]!;

    for (const fromId of currentLayer) {
      const connectCount = nextLayer.length === 1 ? 1 : 1 + rng.nextInt(2);
      const targets = [...nextLayer];
      shuffleArray(targets, rng);
      const chosen = targets.slice(0, connectCount);
      if (chosen.length === 0) chosen.push(nextLayer[0]!);

      for (const toId of chosen) {
        const key = `${fromId}|${toId}`;
        if (existingPairs.has(key)) continue;
        existingPairs.add(key);
        edges.push(makeEdge(`e${edgeIndex++}`, fromId, toId, 1, capacity()));
      }
    }

    for (const toId of nextLayer) {
      const alreadyReached = edges.some(
        (e) => e.targetId === toId && currentLayer.includes(e.sourceId),
      );
      if (!alreadyReached) {
        const fromId = currentLayer[rng.nextInt(currentLayer.length)]!;
        const key = `${fromId}|${toId}`;
        if (!existingPairs.has(key)) {
          existingPairs.add(key);
          edges.push(makeEdge(`e${edgeIndex++}`, fromId, toId, 1, capacity()));
        }
      }
    }
  }

  const graph: Graph = { nodes, edges, directed: true };
  const maxFlowValue = edmondsKarp(graph, 's', 't');
  if (maxFlowValue <= 0) return null;

  const residual = edmondsKarpResidual(graph, 's', 't');
  const flowEdgeIds: string[] = [];

  for (const e of edges) {
    const reverseEdges = residual.get(e.targetId) ?? [];
    const reverseEdge = reverseEdges.find(
      (re) =>
        re.to === e.sourceId && re.originalEdgeId === null && re.reverse.originalEdgeId === e.id,
    );
    if (reverseEdge && reverseEdge.capacity > 0) flowEdgeIds.push(e.id);
  }

  const hasResidualForward = edges.some((e) => {
    const forwardList = residual.get(e.sourceId) ?? [];
    return forwardList.some(
      (re) => re.to === e.targetId && re.originalEdgeId === e.id && re.capacity > 0,
    );
  });
  if (!hasResidualForward) return null;

  return { graph, optimalValue: maxFlowValue, optimalEdgeIds: flowEdgeIds };
}

export function generateMaxFlowGraph(params: DifficultyParams, rng: Rng): GeneratorResult {
  return generateWithRetries(tryGenerateMaxFlow, params, rng, 'MaxFlowGenerator');
}

// ─── Min cut generator (World 5) ────────────────────────────────────────────

function tryGenerateMinCut(params: DifficultyParams, rng: Rng): GeneratorResult | null {
  const flowResult = tryGenerateMaxFlow(params, rng);
  if (!flowResult) return null;

  const { graph } = flowResult;
  const cutEdgeIds = findMinCut(graph, 's', 't');
  if (cutEdgeIds.length === 0) return null;

  const cutCapacity = cutEdgeIds.reduce((sum, id) => sum + capacityOf(graph.edges, id), 0);
  if (cutCapacity <= 0) return null;

  const isTierAtLeastThree = params.nodesPerLayer >= 2;
  if (isTierAtLeastThree && cutEdgeIds.length <= 1) return null;

  return { graph, optimalValue: cutCapacity, optimalEdgeIds: cutEdgeIds };
}

export function generateMinCutGraph(params: DifficultyParams, rng: Rng): GeneratorResult {
  return generateWithRetries(tryGenerateMinCut, params, rng, 'MinCutGenerator');
}

// ─── Matching generator (World 6) ───────────────────────────────────────────

function tryGenerateMatching(params: DifficultyParams, rng: Rng): GeneratorResult | null {
  const sideCount = clampInt(params.nodeCount, 2, 13);

  const leftNodes: GraphNode[] = [];
  const rightNodes: GraphNode[] = [];
  for (let i = 0; i < sideCount; i++) {
    leftNodes.push({
      id: `l${i}`,
      label: letterLabel(i),
      position: { x: 0, y: 0 },
      type: 'source',
    });
  }
  for (let i = 0; i < sideCount; i++) {
    rightNodes.push({
      id: `r${i}`,
      label: `${i + 1}`,
      position: { x: 0, y: 0 },
      type: 'sink',
    });
  }

  const nodes = [...leftNodes, ...rightNodes];
  const leftIds = leftNodes.map((n) => n.id);
  const rightIds = rightNodes.map((n) => n.id);

  const rightPermutation = [...rightIds];
  shuffleArray(rightPermutation, rng);

  const edges: GraphEdge[] = [];
  let edgeIndex = 0;
  const existingPairs = new Set<string>();

  for (let i = 0; i < sideCount; i++) {
    const leftId = leftIds[i]!;
    const rightId = rightPermutation[i]!;
    existingPairs.add(`${leftId}|${rightId}`);
    existingPairs.add(`${rightId}|${leftId}`);
    edges.push(makeEdge(`e${edgeIndex++}`, leftId, rightId));
  }

  let extraAdded = 0;
  let safetyBreak = 0;
  while (extraAdded < params.extraEdges && safetyBreak < 300) {
    safetyBreak++;
    const leftId = leftIds[rng.nextInt(leftIds.length)]!;
    const rightId = rightIds[rng.nextInt(rightIds.length)]!;
    const key = `${leftId}|${rightId}`;
    if (existingPairs.has(key)) continue;

    existingPairs.add(key);
    existingPairs.add(`${rightId}|${leftId}`);
    edges.push(makeEdge(`e${edgeIndex++}`, leftId, rightId));
    extraAdded++;
  }

  const graph: Graph = { nodes, edges, directed: false };
  const matching = maxBipartiteMatching(graph, leftIds, rightIds);
  if (matching.size === 0) return null;

  const matchedEdgeIds: string[] = [];
  for (const [leftId, rightId] of matching) {
    const matchEdge = edges.find(
      (e) =>
        (e.sourceId === leftId && e.targetId === rightId) ||
        (e.sourceId === rightId && e.targetId === leftId),
    );
    if (!matchEdge) {
      throw new Error(`MatchingGenerator: matched edge not found for ${leftId} -> ${rightId}`);
    }
    matchedEdgeIds.push(matchEdge.id);
  }

  let hasAlternativeMatching = false;
  for (const removedEdgeId of matchedEdgeIds) {
    const reducedEdges = edges.filter((e) => e.id !== removedEdgeId);
    const reducedGraph: Graph = { nodes, edges: reducedEdges, directed: false };
    const altMatching = maxBipartiteMatching(reducedGraph, leftIds, rightIds);
    if (altMatching.size === matching.size) {
      hasAlternativeMatching = true;
      break;
    }
  }
  if (!hasAlternativeMatching) return null;

  return { graph, optimalValue: matching.size, optimalEdgeIds: matchedEdgeIds };
}

export function generateMatchingGraph(params: DifficultyParams, rng: Rng): GeneratorResult {
  return generateWithRetries(tryGenerateMatching, params, rng, 'MatchingGenerator');
}

// ─── Interdiction generator (World 7) ───────────────────────────────────────

function interdictionBudgetFor(params: DifficultyParams): number {
  return clampInt(params.interdictionBudget, 1, 3);
}

function tryGenerateInterdiction(params: DifficultyParams, rng: Rng): GeneratorResult | null {
  const budget = interdictionBudgetFor(params);
  const pathCount = budget + 1;

  const totalIntermediates = clampInt(params.nodeCount - 2, pathCount, 24);
  const basePerPath = clampInt(Math.floor(totalIntermediates / pathCount), 1, 5);

  const nodes: GraphNode[] = [{ id: 's', label: 'S', position: { x: 0, y: 0 }, type: 'source' }];
  let nodeIndex = 0;
  const paths: string[][] = [];

  for (let p = 0; p < pathCount; p++) {
    const pathNodes: string[] = [];
    const countForPath =
      p === pathCount - 1
        ? clampInt(totalIntermediates - basePerPath * (pathCount - 1), 1, 5)
        : basePerPath;

    for (let i = 0; i < countForPath; i++) {
      const id = `n${nodeIndex}`;
      nodes.push({
        id,
        label: letterLabelMod(nodeIndex),
        position: { x: 0, y: 0 },
        type: 'normal',
      });
      pathNodes.push(id);
      nodeIndex++;
    }
    paths.push(pathNodes);
  }

  nodes.push({ id: 't', label: 'T', position: { x: 0, y: 0 }, type: 'sink' });

  const edges: GraphEdge[] = [];
  let edgeIndex = 0;
  const existingPairs = new Set<string>();
  const weight = (): number => randomWeightInt(params, rng);

  const addEdge = (from: string, to: string): void => {
    const key = `${from}|${to}`;
    if (existingPairs.has(key)) return;
    existingPairs.add(key);
    existingPairs.add(`${to}|${from}`);
    edges.push(makeEdge(`e${edgeIndex++}`, from, to, weight()));
  };

  for (const path of paths) {
    addEdge('s', path[0]!);
    for (let i = 0; i < path.length - 1; i++) addEdge(path[i]!, path[i + 1]!);
    addEdge(path[path.length - 1]!, 't');
  }

  const allIntermediateIds = paths.flat();

  let crossEdgesAdded = 0;
  let safetyBreak = 0;
  while (crossEdgesAdded < params.extraEdges && safetyBreak < 300) {
    safetyBreak++;
    const a = allIntermediateIds[rng.nextInt(allIntermediateIds.length)]!;
    const b = allIntermediateIds[rng.nextInt(allIntermediateIds.length)]!;
    if (a === b) continue;

    const pathOfA = paths.findIndex((p) => p.includes(a));
    const pathOfB = paths.findIndex((p) => p.includes(b));
    if (pathOfA === pathOfB) continue;

    addEdge(a, b);
    crossEdgesAdded++;
  }

  const graph: Graph = { nodes, edges, directed: false };
  const basePathEdgeIds = dijkstra(graph, 's', 't');
  if (basePathEdgeIds.length === 0) return null;

  const baseLength = basePathEdgeIds.reduce((sum, id) => sum + weightOf(edges, id), 0);

  const interdictedEdgeIds = shortestPathInterdiction(graph, 's', 't', budget);
  const interdictedSet = new Set(interdictedEdgeIds);
  const remainingEdges = edges.filter((e) => !interdictedSet.has(e.id));
  const remainingGraph: Graph = { nodes, edges: remainingEdges, directed: false };
  const postInterdictionPathEdgeIds = dijkstra(remainingGraph, 's', 't');

  const resultingShortestPath =
    postInterdictionPathEdgeIds.length === 0
      ? Infinity
      : postInterdictionPathEdgeIds.reduce((sum, id) => sum + weightOf(remainingEdges, id), 0);

  if (interdictedEdgeIds.length === 0) return null;
  if (resultingShortestPath <= baseLength) return null;

  return { graph, optimalValue: resultingShortestPath, optimalEdgeIds: interdictedEdgeIds };
}

export function generateInterdictionGraph(params: DifficultyParams, rng: Rng): GeneratorResult {
  return generateWithRetries(tryGenerateInterdiction, params, rng, 'InterdictionGenerator');
}

// ─── TSP generator (World 3) ────────────────────────────────────────────────

function distWeight(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  return Math.min(99, Math.max(1, Math.round(dist * 10)));
}

function tspPairKey(a: string, b: string): string {
  return a <= b ? `${a}_${b}` : `${b}_${a}`;
}

function tryGenerateTsp(params: DifficultyParams, rng: Rng): GeneratorResult | null {
  const n = clampInt(params.nodeCount, 5, 9);
  const targetExtra = clampInt(params.extraEdges, 2, 6);

  const positions: Vec2[] = [];
  for (let i = 0; i < n; i++) positions.push({ x: rng.nextDouble(), y: rng.nextDouble() });

  const nodes: GraphNode[] = [];
  for (let i = 0; i < n; i++) {
    nodes.push({
      id: `n${String(i).padStart(2, '0')}`,
      label: letterLabel(i),
      position: positions[i]!,
      type: 'normal',
    });
  }

  const permutation = Array.from({ length: n }, (_, i) => i);
  shuffleArray(permutation, rng);

  const skeletonEdges: GraphEdge[] = [];
  let edgeCounter = 0;

  for (let i = 0; i < n; i++) {
    const a = permutation[i]!;
    const b = permutation[(i + 1) % n]!;
    const w = distWeight(positions[a]!, positions[b]!);
    skeletonEdges.push(
      makeEdge(`e${String(edgeCounter++).padStart(2, '0')}`, nodes[a]!.id, nodes[b]!.id, w),
    );
  }

  const connected = new Set<string>();
  for (const e of skeletonEdges) connected.add(tspPairKey(e.sourceId, e.targetId));

  const extraEdges: GraphEdge[] = [];
  let extraAttempts = 0;
  while (extraEdges.length < targetExtra && extraAttempts < 100) {
    extraAttempts++;
    const a = rng.nextInt(n);
    const b = rng.nextInt(n);
    if (b === a) continue;

    const key = tspPairKey(nodes[a]!.id, nodes[b]!.id);
    if (connected.has(key)) continue;
    connected.add(key);

    const w = distWeight(positions[a]!, positions[b]!);
    extraEdges.push(
      makeEdge(`e${String(edgeCounter++).padStart(2, '0')}`, nodes[a]!.id, nodes[b]!.id, w),
    );
  }

  const graph: Graph = { nodes, edges: [...skeletonEdges, ...extraEdges], directed: false };

  const result = optimalTour(graph);
  if (!result) return null;

  const cycleCount = countHamiltonianCycles(graph, 2);
  if (cycleCount < 2) return null;

  return { graph, optimalValue: result.length, optimalEdgeIds: [...result.tourEdgeIds] };
}

export function generateTspGraph(params: DifficultyParams, rng: Rng): GeneratorResult {
  return generateWithRetries(tryGenerateTsp, params, rng, 'TspGenerator');
}

// ─── Top-level orchestrator ─────────────────────────────────────────────────

const OBJECTIVES: Readonly<Record<ProblemType, string>> = {
  shortestPathPlanar: 'Find the shortest path from S to T.',
  shortestPath: 'Find the shortest path from S to T.',
  mst: 'Connect all nodes with minimum total cost.',
  maxFlow: 'Maximize the flow from S to T.',
  minCut: 'Find the minimum cut separating S from T.',
  matching: 'Find the maximum matching between the two groups.',
  interdiction: 'Remove edges to maximally disrupt the shortest path.',
  tsp: 'Find the shortest tour visiting every substation exactly once.',
};

function callGenerator(type: ProblemType, params: DifficultyParams, rng: Rng): GeneratorResult {
  switch (type) {
    case 'shortestPathPlanar':
    case 'shortestPath':
      return generateShortestPathGraph(params, rng);
    case 'mst':
      return generateMstGraph(params, rng);
    case 'maxFlow':
      return generateMaxFlowGraph(params, rng);
    case 'minCut':
      return generateMinCutGraph(params, rng);
    case 'matching':
      return generateMatchingGraph(params, rng);
    case 'interdiction':
      return generateInterdictionGraph(params, rng);
    case 'tsp':
      return generateTspGraph(params, rng);
  }
}

export interface GenerateRandomLevelInput {
  /** 1-based, must be in [1, WORLDS.length]. */
  readonly worldIndex: number;
  /** Clamped to [1, 10]. */
  readonly difficulty: number;
  /** Embedded in the returned level's id for later replay; drawn randomly when omitted. */
  readonly seed?: number;
}

/**
 * Generates a random level for the given world and difficulty tier.
 *
 * Level id format: `random_w{worldIndex}_d{difficulty}_{seed}`, e.g.
 * `random_w1_d5_482917` — self-contained so the level can be reconstructed
 * with `parseRandomLevelId` + `generateRandomLevel` at any later time.
 */
export function generateRandomLevel(input: GenerateRandomLevelInput): Level {
  const { worldIndex, difficulty } = input;
  if (worldIndex < 1 || worldIndex > WORLDS.length) {
    throw new RangeError(
      `worldIndex must be between 1 and ${WORLDS.length} inclusive; received ${worldIndex}.`,
    );
  }

  const resolvedSeed = input.seed ?? Math.floor(Math.random() * 1_000_000);

  const world = WORLDS[worldIndex - 1]!;
  const type = world.problemType;
  const params = difficultyParamsForType(type, difficulty);
  const rng = new Rng(resolvedSeed);

  const { graph: rawGraph, optimalValue, optimalEdgeIds } = callGenerator(type, params, rng);

  const positionedNodes = layoutNodes(rawGraph.nodes, rawGraph.edges, type, rng);
  const positionedGraph: Graph = {
    nodes: positionedNodes,
    edges: rawGraph.edges,
    directed: rawGraph.directed,
  };

  const thresholds = computeStarThresholds(type, optimalValue);
  const hints = generateHints(type, optimalEdgeIds);

  return {
    id: `random_w${worldIndex}_d${difficulty}_${resolvedSeed}`,
    worldIndex,
    levelIndex: 0,
    title: `${world.name} Challenge`,
    objective: OBJECTIVES[type],
    type,
    graph: positionedGraph,
    optimalValue,
    optimalSolutionEdgeIds: optimalEdgeIds,
    thresholds,
    hints,
    budget: type === 'interdiction' ? interdictionBudgetFor(params) : 2,
    mechanics: [],
    nodeStyle: 'building',
    backgroundBuildings: [],
    backgroundPatches: [],
    useDistanceWeights: false,
  };
}

export interface ParsedRandomLevelId {
  readonly worldIndex: number;
  readonly difficulty: number;
  readonly seed: number;
}

export function isRandomLevel(levelId: string): boolean {
  return levelId.startsWith('random_');
}

function parseFullInt(value: string): number | null {
  return /^\d+$/.test(value) ? Number(value) : null;
}

export function parseRandomLevelId(levelId: string): ParsedRandomLevelId | null {
  if (!isRandomLevel(levelId)) return null;

  const parts = levelId.split('_');
  if (parts.length !== 4) return null;

  const [, worldPart, diffPart, seedPart] = parts;
  if (!worldPart || !diffPart || seedPart === undefined) return null;
  if (!worldPart.startsWith('w') || !diffPart.startsWith('d')) return null;

  const worldIndex = parseFullInt(worldPart.slice(1));
  const difficulty = parseFullInt(diffPart.slice(1));
  const seed = parseFullInt(seedPart);
  if (worldIndex === null || difficulty === null || seed === null) return null;

  return { worldIndex, difficulty, seed };
}
